import React, { useEffect, useRef, useState } from 'react';
import { Group, Loader, Stack } from '@mantine/core';
import ColorButton from '../neoverv/components/Module/General/Buttons/ColorButton';
import { LEGAL_DOCUMENT_LINKS } from '../helpers/legalConsent';

// Pause between two consent bubbles, so the onboarding is typed out at the pace
// of a chat bot instead of appearing as one wall of text.
const THINKING_DELAY_MS = 1400;

// The staged reveal is a first-impression effect only: once it has played in
// this session, reopening the chat renders every bubble immediately (the
// consent screen stays up until the account accepts, so it is mounted again on
// every open until then).
let introPlayed = false;

// Shown in place of the chat history until the account has accepted the AI
// terms (account.consents.aiConsentAcceptedOn). Rendered as chat bubbles so the
// onboarding reads like the first messages of the conversation.
const ConsentBubble = ({ children }) => (
  <Stack align={'flex-start'}>
    <Group justify={'left'}>
      <Stack gap={0} style={{ marginBottom: '1rem' }} align={'flex-start'}>
        <p className={'ai-chat-consent-bubble'}>
          <span className={'ai-chat-bubble-label'}>hellohousing KI</span>
          <br style={{ marginBottom: 10 }} />
          <span className={'ai-chat-consent-bubble-text'}>{children}</span>
        </p>
      </Stack>
    </Group>
  </Stack>
);

// Same dots loader the chat itself shows while a reply is being generated.
const ThinkingBubble = () => (
  <Stack align={'flex-start'}>
    <Group justify={'left'} style={{ marginBottom: '1rem' }}>
      <Loader size={'xs'} type={'dots'} color={'#5555FF'} />
    </Group>
  </Stack>
);

// Rough cost of one chat request in KI-Einheiten, as reported per reply in
// usage.aiCredits: a plain question costs about 0.5 for the first message and
// less afterwards because the input is cached, while requests that call heavy
// tools (e.g. fetching a utility billing) reach 2 and occasionally more. The
// request range in the copy is the included credits divided by these two
// figures, so re-tune them if the agent's tools or pricing change noticeably.
const CREDITS_PER_LIGHT_REQUEST = 0.5;
const CREDITS_PER_HEAVY_REQUEST = 2;

const hasIncludedCredits = (includedCredits) =>
  typeof includedCredits === 'number' && includedCredits > 0;

// `includedCredits` comes from the /creditUsage endpoint; until it has loaded
// (or when the account has no included credits) the placeholder from the copy
// is shown instead of a wrong number.
const formatIncludedCredits = (includedCredits) =>
  hasIncludedCredits(includedCredits)
    ? Math.round(includedCredits).toLocaleString('de-DE')
    : 'XX';

// Rounded down to steps of 5 so an estimate doesn't read as precise, and so it
// never promises more requests than the credits cover.
const roundDownForEstimate = (count) =>
  count >= 10 ? Math.floor(count / 5) * 5 : Math.max(Math.floor(count), 1);

const formatRequestRange = (includedCredits) => {
  if (!hasIncludedCredits(includedCredits)) {
    return 'XX–XX';
  }
  const fewest = roundDownForEstimate(includedCredits / CREDITS_PER_HEAVY_REQUEST);
  const most = roundDownForEstimate(includedCredits / CREDITS_PER_LIGHT_REQUEST);
  return `${fewest.toLocaleString('de-DE')}–${most.toLocaleString('de-DE')}`;
};

// `onReveal` lets the host scroll the chat box down as bubbles come in.
export default function AiChatConsent({ includedCredits = null, loading = false, onAccept, onReveal }) {
  const bubbles = [
    <>Herzlich willkommen bei der hellohousing KI!</>,

    <>
      Die hellohousing KI ist Ihr persönlicher Assistent, der bei allen Aufgaben der digitalen Immobilienverwaltung in hellohousing unterstützt:
      <ul className={'ai-chat-consent-list'}>
        <li>Mieteinheiten und Mieter anlegen</li>
        <li>Dokumente und Belege auslesen, anlegen und zuordnen</li>
        <li>Zahlungen, Kosten und Abrechnungen prüfen</li>
        <li>Zählerstände und Verbrauchsdaten auswerten</li>
        <li>Informationen zusammenfassen und verständlich aufbereiten</li>
        <li>Daten bereitstellen und exportieren (z.B. Excel und PDF)</li>
        <li>Funktionen und Abläufe von hellohousing erklären</li>
        <li>Bei Fragen und Problemen unterstützen</li>
      </ul>
      Sie können dem KI-Assistenten einfach Fragen stellen oder Aufgaben erteilen.
      Wir erweiteren den KI-Assistenten fortlaufend um neue Fähigkeiten.
    </>,

    <>
      Für die Nutzung des KI-Assistenten gelten die aktuelle{' '}
      <span
        className={'intercom-chat-link'}
        onClick={() => window.open(LEGAL_DOCUMENT_LINKS.dataPolicy, '_blank')}>
        hellohousing Datenschutzvereinbarung
      </span>{' '}
      und die{' '}
      <span
        className={'intercom-chat-link'}
        onClick={() => window.open(LEGAL_DOCUMENT_LINKS.dataProcessing, '_blank')}>
        Vereinbarung zur Auftragsverarbeitung
      </span>
      .
    </>,

    <>
      {`Ihr Abonnement beinhaltet ohne Zusatzkosten jeden Monat neu ${formatIncludedCredits(includedCredits)} KI-Einheiten zur Nutzung für KI-Anfragen. Das entspricht je nach Komplexität und Umfang der Anfragen etwa ${formatRequestRange(includedCredits)} Anfragen an den hellohousing KI-Assistenten.

      Nach Verbrauch der im Abonnement enthaltenen Einheiten berechnet hellohousing 2 EUR für jeweils weitere 100 KI-Einheiten. Diese werden bei Nichtgebrauch auf den Folgemonat übertragen. Sie können die aktuelle Nutzung der KI-Einheiten jederzeit oben rechts im Chat-Fenster einsehen.`}
    </>,

    <>
      Klicken Sie auf "Verstanden und Fortfahren", um die Nutzungsbedingungen zu akzeptieren und mit der Nutzung des KI-Assistenten zu beginnen.
    </>,
  ];

  const totalBubbles = useRef(bubbles.length);
  totalBubbles.current = bubbles.length;

  const [visibleCount, setVisibleCount] = useState(() => (introPlayed ? bubbles.length : 0));

  useEffect(() => {
    if (introPlayed) {
      return undefined;
    }
    // Marked as played on the first mount rather than on completion, so closing
    // the chat mid-sequence doesn't restart it on the next open.
    introPlayed = true;
    let shown = 0;
    let timeoutId = null;
    const revealNext = () => {
      shown += 1;
      setVisibleCount(shown);
      if (shown < totalBubbles.current) {
        timeoutId = setTimeout(revealNext, THINKING_DELAY_MS);
      }
    };
    timeoutId = setTimeout(revealNext, THINKING_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (visibleCount > 0 && onReveal) {
      onReveal();
    }
  }, [visibleCount]);

  return (
    <Stack gap={10} className={'ai-chat-consent'}>
      {bubbles.slice(0, visibleCount).map((bubble, index) => (
        <ConsentBubble key={`ai-chat-consent-bubble-${index}`}>{bubble}</ConsentBubble>
      ))}

      {visibleCount < bubbles.length ?
      <ThinkingBubble/>
      :
      <Group justify={'center'} style={{ width: '100%', marginBottom: '1rem' }}>
        <div>
          <ColorButton
            className={'purple-btn'}
            text={'Verstanden und Fortfahren'}
            showLoader={loading}
            disabled={loading}
            onClick={() => onAccept()}
          />
        </div>
      </Group>}
    </Stack>
  );
}
